import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getVerticalCoverImage } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import {
  getAgeRangePublicRoute,
  isContentItemAllowedForAgeRange,
} from "../../utils/ageRange.util";
import { useInputMode } from "../../utils/hooks/useInputMode";
import { useContentImpression } from "../../utils/hooks/useContentImpression";

var MovieItem = function MovieItem(props) {
  var item = props.item;
  var scrollContainerRef = props.scrollContainerRef;

  var navigate = useNavigate();
  var contentPhotos = item.contentPhotos;
  var slug = item.slug;
  var tags = item.tags;
  var kind = item.kind;
  var contentType = item.contentType;

  var focusResult = useFocusable({
    onEnterPress: function () {
      navigate("/" + (contentType?.kind || kind) + "/" + slug);
    },
    onFocus: props.onFocus,
  });
  var focused = focusResult.focused;
  var ref = focusResult.ref;
  var focusSelf = focusResult.focusSelf;
  useContentImpression(ref, item, {
    surface: props.surface || "content_row",
    source: props.source || "slider",
    containerKey: props.containerKey || props.id || "",
    position: props.position || 0,
  });

  // Input mode tracking for scroll behavior
  var inputMode = useInputMode();
  var isMouseFocus = inputMode.isMouseFocus;
  var setMouseFocus = inputMode.setMouseFocus;

  // Scroll into view when focused using native scrolling (skip for mouse hover)
  useEffect(function () {
    if (focused && ref.current && scrollContainerRef?.current && !isMouseFocus) {
      var element = ref.current;
      var container = scrollContainerRef.current;

      // Try scrollIntoView with options first (modern browsers)
      try {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      } catch (e) {
        // Fallback for older browsers - manual scroll calculation
        var elementRect = element.getBoundingClientRect();
        var containerRect = container.getBoundingClientRect();
        var elementCenter = elementRect.left + (elementRect.width / 2);
        var containerCenter = containerRect.left + (containerRect.width / 2);
        var scrollOffset = elementCenter - containerCenter;
        container.scrollLeft = container.scrollLeft + scrollOffset;
      }
    }
  }, [focused, isMouseFocus]);

  // Get optimized image with LQIP and WebP support
  var imageData = getVerticalCoverImage(contentPhotos);

  var isSeries = contentType?.kind === "series" || kind === "series";

  var handleNavigate = function () {
    navigate("/" + (contentType?.kind || kind) + "/" + slug);
  };

  return (
    <div className={"legacy-horizontal-card-wrapper" + (props.isLast ? " legacy-horizontal-card-wrapper-last" : "")}>
      {isSeries && (
        <div className="legacy-horizontal-series-pseudo">
          <img className="legacy-horizontal-series-pseudo-image" src="/serial_pseudo.svg" alt="" />
        </div>
      )}
      <div
        ref={ref}
        className={"legacy-horizontal-card" + (focused ? " is-focused" : "")}
        onMouseEnter={function () { setMouseFocus(true); focusSelf(); }}
      >
        <OptimizedImage
          src={imageData.src}
          className="cursor-pointer"
          style={{
            width: "100%",
            minHeight: "350px",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: focused ? 10 : 0,
            borderRadius: "12px",
          }}
          onClick={handleNavigate}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
        <div className="legacy-horizontal-card-overlay">
          <span className="legacy-horizontal-card-title">{item.title}</span>
          <span className="legacy-horizontal-card-year">{item.year}</span>
        </div>
      </div>
    </div>
  );
};

export var MoviesSlider = function MoviesSlider(props) {
  const [seletedIndex, setSelectedIndex] = useState(0);
  var movies = props.movies;
  var onFocus = props.onFocus;
  var shapeType = props.shapeType || "movie";
  var id = props.id;
  var index = props.index;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return isContentItemAllowedForAgeRange(x, ageRange);
  });

  var focusResult = useFocusable({
    onFocus: onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
  });
  var ref = focusResult.ref;
  var focusKey = focusResult.focusKey;

  var scrollingRef = useRef(null);

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={"legacy-horizontal-row" + (shapeType === "music" ? " legacy-horizontal-row-music" : "")}
        tabIndex={seletedIndex}
      >
        <div className="legacy-horizontal-row-scroller" ref={scrollingRef}>
          <div className="legacy-horizontal-row-content">
            {_items.map(function (item, idx) {
              return (
                <MovieItem
                  item={item}
                  key={"ms_" + (item?.slug || idx) + "_" + id + "_" + idx}
                  scrollContainerRef={scrollingRef}
                  shapeType={shapeType}
                  isLast={idx === _items.length - 1}
                  surface={props.surface || "content_row"}
                  source={props.source || "slider"}
                  containerKey={props.containerKey || id || props.title || ""}
                  position={idx}
                  id={id}
                />
              );
            })}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
